<template>
    <div>
        <h2>{{ $t("settings.channels.Business hours") }}</h2>
        <p>{{ $t("settings.channels.business-hours-description") }}</p>

        <validation-provider #default="{ errors }" name="timezone" rules="required">
            <b-form-group
                id="fieldset-timezone"
                :label="$t('locale.timezone')"
                :state="errors.length > 0 ? false : null"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="timezone"
            >
                <v-select
                    id="timezone"
                    v-model="getSettingByName('timezone', 'Europe/Amsterdam').value"
                    :options="timezones"
                    :state="errors.length > 0 ? false : null"
                    variant="primary"
                />
                <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
        </validation-provider>

        <b-form-group
            v-for="day in days"
            :id="`fieldset-businessHours_active_${day}`"
            :key="`fieldset-businessHours_active_${day}`"
            :label="$t(`locale.days.${day}`)"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="timezone"
        >
            <b-container>
                <b-row>
                    <b-col md="2" sm="12">
                        <b-form-checkbox
                            v-model="getSettingByName(`businessHours_active_${day.toLowerCase()}`).value"
                            :name="`businessHours_active_${day}`"
                            inline
                            switch
                            unchecked-value="0"
                            value="1"
                        />
                    </b-col>
                    <b-col md="5" sm="12">
                        <b-form-timepicker
                            v-model="getSettingByName(`businessHours_start_${day.toLowerCase()}`).value"
                            :locale="this && this.$i18n.locale"
                            :name="`businessHours_start_${day.toLowerCase()}`"
                        />
                    </b-col>
                    <b-col md="5" sm="12">
                        <b-form-timepicker
                            v-model="getSettingByName(`businessHours_end_${day.toLowerCase()}`).value"
                            :locale="this && this.$i18n.locale"
                            :name="`businessHours_end_${day.toLowerCase()}`"
                        />
                    </b-col>
                </b-row>
            </b-container>
        </b-form-group>

        <hr />

        <h3>{{ $t("settings.channels.custom-closing-dates.title") }}</h3>
        <p>{{ $t("settings.channels.custom-closing-dates.description") }}</p>

        <div v-for="[date, settings] in Object.entries(customClosingDates)" :key="`custom-closing-date-${date}`">
            <validation-provider #default="{ errors }" name="date" rules="required">
                <b-form-group
                    :id="`${date}-date`"
                    :description="$t('settings.channels.custom-closing-dates.date.description')"
                    :label="$t('settings.channels.custom-closing-dates.date.label')"
                    :state="errors.length > 0 ? false : null"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="name"
                >
                    <b-input-group>
                        <v-date-picker
                            :id="`custom-hours-datepicker_${date}`"
                            v-model="settings.date"
                            :disabled="!Boolean(settings.date)"
                            :disabled-dates="Boolean(settings.date) ? disableAllDates : excludedDates"
                            :locale="$i18n.locale"
                            :min-date="new Date()"
                            @input="addNewSettingsToList(settings, $event)"
                        />
                        <b-button
                            :title="$t('settings.channels.custom-closing-dates.delete')"
                            type="button"
                            variant="flat-secondary"
                            @click="deleteSettings(date)"
                        >
                            <font-awesome-icon :icon="['far', 'times']" class="cursor-pointer" size="1x" />
                        </b-button>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>
            <b-form-group
                :id="`${date}-open`"
                :description="$t('settings.channels.custom-closing-dates.open.description')"
                :label="$t('settings.channels.custom-closing-dates.open.label')"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="name"
            >
                <b-form-checkbox
                    v-model="settings.open.value"
                    :name="`customClosingDate_open_${date}`"
                    inline
                    switch
                    unchecked-value="0"
                    value="1"
                />
            </b-form-group>
            <div v-if="Number(settings.open.value)">
                <b-form-group
                    :id="`${date}-start`"
                    :description="$t('settings.channels.custom-closing-dates.start.description')"
                    :label="$t('settings.channels.custom-closing-dates.start.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="name"
                >
                    <b-form-timepicker
                        v-model="settings.start.value"
                        :locale="this && this.$i18n.locale"
                        :name="`customClosingDate_start_${date}`"
                    />
                </b-form-group>
                <b-form-group
                    :id="`${date}-end`"
                    :description="$t('settings.channels.custom-closing-dates.end.description')"
                    :label="$t('settings.channels.custom-closing-dates.end.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="name"
                >
                    <b-form-timepicker
                        v-model="settings.end.value"
                        :locale="this && this.$i18n.locale"
                        :name="`customClosingDate_end_${date}`"
                    />
                </b-form-group>
            </div>

            <hr />
        </div>
        <b-button variant="primary" @click="addNewCustomClosingDate()">
            {{ $t("settings.channels.custom-closing-dates.add") }}
        </b-button>
        <hr />
    </div>
</template>

<script>
import {
    BContainer,
    BCol,
    BRow,
    BCard,
    BFormGroup,
    BFormCheckbox,
    BFormTimepicker,
    BInputGroup,
    BFormDatepicker,
    BButton,
} from "bootstrap-vue";
import useChannelSettings from "../useChannelSettings";
import moment from "moment-timezone";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import * as lodash from "lodash";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BContainer,
        BCol,
        BRow,
        BFormGroup,
        BFormCheckbox,
        BCard,
        ValidationProvider,
        ValidationObserver,
        BFormTimepicker,
        vSelect,
        BInputGroup,
        BFormDatepicker,
        BButton,
    },
    props: {
        channelData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            required,
            timezones: moment.tz.names(),
            days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        };
    },
    methods: {
        deleteSettings(date) {
            const newObject = {
                ...this.customClosingDates,
            };
            delete newObject[date];
            this.customClosingDates = newObject;

            const regex = new RegExp(`customClosingDate_((open)|(start)|(end))_${date}`);
            lodash.remove(this.channelData.settings, (setting) => regex.test(setting.name));
        },
        addNewSettingsToList(settingsObject, date) {
            const dateString = `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${date.getDate()}`;

            settingsObject.open.name += dateString;
            settingsObject.start.name += dateString;
            settingsObject.end.name += dateString;

            delete settingsObject.open.id;
            delete settingsObject.start.id;
            delete settingsObject.end.id;

            this.customClosingDates[dateString] = settingsObject;
            delete this.customClosingDates["_"];

            this.channelData.settings.push(...[settingsObject.open, settingsObject.start, settingsObject.end]);
        },
        addNewCustomClosingDate() {
            if (this.customClosingDates["_"]) {
                return this.toast.warning(this.$t("settings.channels.custom-closing-dates.using-existing"));
            }

            const date = ref("");
            const customClosingDateObject = ref({
                date: date,
                open: {
                    id: `temp-${Math.floor(Math.random() * 999999)}`,
                    name: `customClosingDate_open_`,
                    value: "0",
                    channelId: this.channelData.id,
                },
                start: {
                    id: `temp-${Math.floor(Math.random() * 999999)}`,
                    name: `customClosingDate_start_`,
                    value: "0:0",
                    channelId: this.channelData.id,
                },
                end: {
                    id: `temp-${Math.floor(Math.random() * 999999)}`,
                    name: `customClosingDate_end_`,
                    value: "0:0",
                    channelId: this.channelData.id,
                },
            });
            // Add the property like this because ref() doesn't update everything when adding a property to an object
            this.customClosingDates = {
                ...this.customClosingDates,
                _: customClosingDateObject.value,
            };
        },
    },
    setup(props) {
        const toast = useToast();

        const customClosingDateSettings = props.channelData.settings.filter((setting) =>
            setting.name.includes("customClosingDate")
        );
        const customClosingDates = ref({});

        const { getSettingByName } = useChannelSettings(props.channelData);

        customClosingDateSettings.forEach((setting) => {
            const [_, settingType, date] = setting.name.split("_");

            if (!customClosingDates.value[date]) {
                customClosingDates.value[date] = {
                    date: new Date(date),
                    open: {
                        id: `temp-${Math.floor(Math.random() * 999999)}`,
                        name: `customClosingDate_open_${date}`,
                    },
                    start: {
                        id: `temp-${Math.floor(Math.random() * 999999)}`,
                        name: `customClosingDate_start_${date}`,
                    },
                    end: {
                        id: `temp-${Math.floor(Math.random() * 999999)}`,
                        name: `customClosingDate_end_${date}`,
                    },
                };
            }

            customClosingDates.value[date][settingType] = setting;
        });

        const excludedDates = computed(() => {
            const dates = Object.keys(customClosingDates.value);
            return dates.reduce((list, date) => {
                if (date !== "_") {
                    const dateObject = new Date(date);
                    list.push({
                        start: dateObject,
                        end: dateObject,
                    });
                }

                return list;
            }, []);
        });

        const disableAllDates = [
            {
                start: new Date(),
                end: null,
            },
        ];

        return {
            customClosingDates,
            disableAllDates,
            excludedDates,
            getSettingByName,

            toast,
        };
    },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
